import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  Collapse,
  Chip,
} from '@mui/material';
import { Add, Edit, Close, ChatBubbleOutline, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import StandardPagination from './Pagination/StandardPagination';
import SavingOverlay from './SavingOverlay';
import ConfirmModal from './modals/ConfirmModal';
import { Toast } from './Toast';
import { useToast } from '../hooks/useToast';
import {
  patientDiagnosesService,
  mapPatientDiagnosisError,
  PatientDiagnosis,
  DiagnosisComment,
  DIAGNOSIS_COMMENTS_MAX_LENGTH,
} from '../services/patientDiagnosesService';
import { cidsService, CidOption } from '../services/cidsService';
import { colors, typography, inputs } from '../theme/designSystem';
import ClearFiltersButton from './ClearFiltersButton';
import { filterFieldSx, searchFieldSx, dateFieldSx, filterActionButtonSx } from '../theme/filterStyles';

/**
 * Aba Diagnóstico do cadastro do paciente (RN-535): CIDs reais do paciente. Dado clínico —
 * permissão própria `patient-diagnosis` (Administrador e Profissional); quem vê a aba também
 * cria/edita/exclui. Exclusão é LÓGICA (vai para os inativos); escrita dupla no servidor.
 */
interface Props {
  patientId: string;
}

const DEFAULT_PAGE_SIZE = 10;
const MIN_CID_SEARCH_LENGTH = 2;

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

const fieldSx = {
  backgroundColor: '#fff',
  flex: '1 1 170px',
  minWidth: 150,
  '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '40px' },
};

const Dot: React.FC = () => (
  <Typography component="span" sx={{ color: colors.textSecondary, fontSize: '0.9rem' }}>•</Typography>
);

const cidLabel = (option: CidOption): string => `${option.cidSubCategory} - ${option.cidDescription}`;

// Ações do Administrador sobre diagnóstico de outra pessoa (RN-535g): a thread mostra quem mexeu
const ADMIN_KIND_LABELS: Record<string, string> = {
  admin_update: 'Alterado pelo Administrador',
  admin_inactivation: 'Inativado pelo Administrador',
  admin_reactivation: 'Reativado pelo Administrador',
};

const editHint = (d: PatientDiagnosis): string => {
  if (!d.active) return 'Diagnóstico inativo — reative para editar';
  if (!d.canEdit) return 'Só quem registrou pode editar. Você pode comentar.';
  return d.requiresReason ? 'Editar diagnóstico de outra pessoa (exige justificativa)' : 'Editar diagnóstico';
};

const statusHint = (d: PatientDiagnosis): string => {
  if (!d.canEdit) return 'Só quem registrou pode inativar ou reativar. Você pode comentar.';
  if (d.active) {
    return d.requiresReason
      ? 'Clique para inativar (justificativa obrigatória)'
      : 'Clique para inativar (com justificativa opcional)';
  }
  return d.requiresReason ? 'Clique para reativar (justificativa obrigatória)' : 'Clique para reativar';
};

const PatientDiagnosisTab: React.FC<Props> = ({ patientId }) => {
  const [items, setItems] = useState<PatientDiagnosis[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [status, setStatus] = useState('active');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<PatientDiagnosis | null>(null);
  const [cid, setCid] = useState<CidOption | null>(null);
  const [cidOptions, setCidOptions] = useState<CidOption[]>([]);
  const [cidSearch, setCidSearch] = useState('');
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [toDeactivate, setToDeactivate] = useState<PatientDiagnosis | null>(null);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  // Administrador mexendo em diagnóstico de outra pessoa (RN-535g): justificativa obrigatória
  const [editReason, setEditReason] = useState('');
  const [toReactivate, setToReactivate] = useState<PatientDiagnosis | null>(null);
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivating, setReactivating] = useState(false);

  // Thread de comentários (RN-535d): um diagnóstico expandido por vez, carregado sob demanda
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [threadComments, setThreadComments] = useState<DiagnosisComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<DiagnosisComment | null>(null);
  const [deletingComment, setDeletingComment] = useState(false);

  const { toast, showToast, hideToast } = useToast();
  const loadSeqRef = useRef(0);

  const invalidRange = !!from && !!to && to < from;

  const load = useCallback(async () => {
    if (invalidRange) {
      setItems([]);
      setTotalCount(0);
      setError('A data final não pode ser anterior à data inicial.');
      setLoading(false);
      return;
    }
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const result = await patientDiagnosesService.getByPatient(patientId, {
        status,
        from: from || undefined,
        to: to || undefined,
        search: search.trim() || undefined,
        pageNumber: page,
        pageSize,
      });
      if (seq !== loadSeqRef.current) return;
      setItems(result.items);
      setTotalCount(result.totalCount);
    } catch {
      if (seq !== loadSeqRef.current) return;
      setError('Não foi possível carregar os diagnósticos do paciente.');
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [patientId, status, search, from, to, page, pageSize, invalidRange]);

  useEffect(() => {
    load();
  }, [load]);

  // Busca server-side no catálogo de CIDs (~12,4 mil linhas) com debounce
  useEffect(() => {
    const term = cidSearch.trim();
    if (term.length < MIN_CID_SEARCH_LENGTH || (cid && cidLabel(cid) === term)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      cidsService.search(term)
        .then(setCidOptions)
        .catch(() => setCidOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [cidSearch, cid]);

  const changeFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };
  const hasFilters = status !== 'active' || !!search.trim() || !!from || !!to;
  const clearFilters = () => {
    setStatus('active');
    setSearch('');
    setFrom('');
    setTo('');
    setPage(1);
  };

  const openCreate = () => {
    setEditing(null);
    setCid(null);
    setCidOptions([]);
    setCidSearch('');
    setComments('');
    setSaveError('');
    setEditReason('');
    setIsModalOpen(true);
  };
  const openEdit = (diagnosis: PatientDiagnosis) => {
    setEditing(diagnosis);
    const current: CidOption | null = diagnosis.cidId
      ? { id: diagnosis.cidId, cidSubCategory: diagnosis.cidSubcategory, cidDescription: diagnosis.cidDescription }
      : null;
    setCid(current);
    setCidOptions(current ? [current] : []);
    setCidSearch(current ? cidLabel(current) : '');
    setComments(diagnosis.comments);
    setSaveError('');
    setEditReason('');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    if (saving) return;
    setIsModalOpen(false);
    setEditing(null);
    setCid(null);
    setCidSearch('');
    setComments('');
    setSaveError('');
    setEditReason('');
  };

  const handleSave = async () => {
    if (!cid) {
      setSaveError('Selecione um CID do catálogo.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      if (editing) {
        await patientDiagnosesService.update(
          editing.id, cid.id, comments.trim() || null, editReason.trim() || null);
        showToast('Diagnóstico atualizado.', 'success');
      } else {
        await patientDiagnosesService.create(patientId, cid.id, comments.trim() || null);
        showToast('Diagnóstico gravado.', 'success');
      }
      setSaving(false);
      closeModal();
      if (page === 1) load(); else setPage(1);
    } catch (err: any) {
      setSaving(false);
      setSaveError(mapPatientDiagnosisError(err));
    }
  };

  const loadComments = async (diagnosisId: string) => {
    setCommentsLoading(true);
    try {
      setThreadComments(await patientDiagnosesService.getComments(diagnosisId));
    } catch {
      showToast('Não foi possível carregar os comentários.', 'error');
    } finally {
      setCommentsLoading(false);
    }
  };

  const toggleComments = (diagnosis: PatientDiagnosis) => {
    if (expandedId === diagnosis.id) {
      setExpandedId(null);
      setThreadComments([]);
      return;
    }
    setExpandedId(diagnosis.id);
    setThreadComments([]);
    setNewComment('');
    loadComments(diagnosis.id);
  };

  const handleAddComment = async () => {
    const text = newComment.trim();
    if (!expandedId || !text) return;
    setSendingComment(true);
    try {
      await patientDiagnosesService.addComment(expandedId, text);
      setNewComment('');
      loadComments(expandedId);
      load(); // atualiza a contagem no card
    } catch (err: any) {
      showToast(mapPatientDiagnosisError(err), 'error');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    setDeletingComment(true);
    try {
      await patientDiagnosesService.deleteComment(commentToDelete.id);
      setCommentToDelete(null);
      if (expandedId) loadComments(expandedId);
      load();
    } catch (err: any) {
      showToast(mapPatientDiagnosisError(err), 'error');
    } finally {
      setDeletingComment(false);
    }
  };

  const handleDeactivate = async () => {
    if (!toDeactivate) return;
    setDeactivating(true);
    try {
      await patientDiagnosesService.deactivate(toDeactivate.id, deactivateReason.trim() || null);
      showToast('Diagnóstico inativado.', 'success');
      if (expandedId === toDeactivate.id) loadComments(toDeactivate.id);
      setToDeactivate(null);
      setDeactivateReason('');
      load();
    } catch (err: any) {
      showToast(mapPatientDiagnosisError(err), 'error');
    } finally {
      setDeactivating(false);
    }
  };

  const reactivate = async (diagnosis: PatientDiagnosis, reason: string | null) => {
    try {
      await patientDiagnosesService.reactivate(diagnosis.id, reason);
      showToast('Diagnóstico reativado.', 'success');
      if (expandedId === diagnosis.id) loadComments(diagnosis.id);
      load();
    } catch (err: any) {
      showToast(mapPatientDiagnosisError(err), 'error');
    }
  };

  // Autor reativa direto; Administrador em registro de terceiro passa pela justificativa
  const handleReactivateClick = (diagnosis: PatientDiagnosis) => {
    if (diagnosis.requiresReason) {
      setReactivateReason('');
      setToReactivate(diagnosis);
      return;
    }
    reactivate(diagnosis, null);
  };

  const handleReactivateConfirm = async () => {
    if (!toReactivate) return;
    setReactivating(true);
    await reactivate(toReactivate, reactivateReason.trim() || null);
    setReactivating(false);
    setToReactivate(null);
    setReactivateReason('');
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const changed = editing
    ? cid?.id !== editing.cidId || comments.trim() !== editing.comments.trim()
    : !!cid;
  // Sem justificativa o Administrador não salva alteração de diagnóstico alheio (RN-535g)
  const dirty = changed && (!editing?.requiresReason || !!editReason.trim());

  return (
    <div className="tab-content-section">
      {/* Filtros reativos + novo diagnóstico */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: '21px' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="CID, descrição ou observação"
          value={search}
          onChange={(e) => changeFilter(setSearch)(e.target.value)}
          inputProps={{ maxLength: 100 }}
          InputLabelProps={{ shrink: true }}
          sx={searchFieldSx}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => changeFilter(setStatus)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={filterFieldSx}
        >
          <MenuItem value="active">Ativos</MenuItem>
          <MenuItem value="inactive">Inativos</MenuItem>
          <MenuItem value="all">Todos</MenuItem>
        </TextField>
        <TextField
          type="date"
          size="small"
          label="De"
          value={from}
          onChange={(e) => changeFilter(setFrom)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <TextField
          type="date"
          size="small"
          label="Até"
          value={to}
          onChange={(e) => changeFilter(setTo)(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={dateFieldSx}
        />
        <ClearFiltersButton onClick={clearFilters} disabled={!hasFilters} />
        <Tooltip title="Novo diagnóstico" arrow>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton
              onClick={openCreate}
              sx={filterActionButtonSx}
            >
              <Add />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2, px: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
          <strong>{totalCount}</strong> {totalCount === 1 ? 'diagnóstico encontrado' : 'diagnósticos encontrados'}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: colors.error, fontSize: '0.9rem', px: 1, py: 2 }}>{error}</Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ color: colors.textSecondary, fontSize: '0.9rem', textAlign: 'center', py: 4 }}>
          Nenhum diagnóstico encontrado para este paciente.
        </Typography>
      ) : (
        <div className="diagnosis-list">
          {items.map(diagnosis => (
            <Box
              key={diagnosis.id}
              sx={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', mb: 2, overflow: 'hidden' }}
            >
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: colors.text }}>
                      {formatIsoDate(diagnosis.createdAt)}
                    </Typography>
                    <Dot />
                    <Typography sx={{ color: colors.text, fontSize: '0.9rem' }}>{diagnosis.createdByName}</Typography>
                    <Chip
                      label={diagnosis.active ? 'Ativo' : 'Inativo'}
                      size="small"
                      onClick={diagnosis.canEdit
                        ? () => {
                          if (diagnosis.active) {
                            setDeactivateReason('');
                            setToDeactivate(diagnosis);
                          } else {
                            handleReactivateClick(diagnosis);
                          }
                        }
                        : undefined}
                      title={statusHint(diagnosis)}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        cursor: diagnosis.canEdit ? 'pointer' : 'default',
                        backgroundColor: diagnosis.active ? '#28a745' : '#adb5bd',
                      }}
                    />
                    {!diagnosis.isAuthor && (
                      <Chip
                        label="De outro profissional"
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, color: colors.textSecondary, borderColor: '#dee2e6' }}
                      />
                    )}
                  </Box>
                  <Typography sx={{ color: colors.text, lineHeight: 1.6, fontSize: '0.875rem', overflowWrap: 'anywhere' }}>
                    <strong>CID {diagnosis.cidSubcategory} - {diagnosis.cidDescription}</strong>
                    {diagnosis.comments ? ` - ${diagnosis.comments}` : ''}
                  </Typography>

                  {/* Thread de comentários (RN-535d) */}
                  <Typography
                    component="button"
                    type="button"
                    onClick={() => toggleComments(diagnosis)}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, background: 'none', border: 'none', padding: 0, mt: 1, font: 'inherit', fontSize: '0.8rem', fontWeight: 600, color: colors.primary, cursor: 'pointer' }}
                  >
                    <ChatBubbleOutline sx={{ fontSize: '1rem' }} />
                    {expandedId === diagnosis.id
                      ? 'Fechar comentários'
                      : `Abrir comentários (${diagnosis.commentsCount})`}
                    {expandedId === diagnosis.id
                      ? <ExpandLess sx={{ fontSize: '1.1rem' }} />
                      : <ExpandMore sx={{ fontSize: '1.1rem' }} />}
                  </Typography>
                  <Collapse in={expandedId === diagnosis.id} unmountOnExit>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #f1f3f5' }}>
                      <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mb: 1.5 }}>
                        Comentários visíveis a <strong>todos os profissionais e administradores</strong> com acesso ao Diagnóstico.
                      </Typography>
                      {commentsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                          <CircularProgress size={20} sx={{ color: colors.primary }} />
                        </Box>
                      ) : threadComments.length === 0 ? (
                        <Typography sx={{ fontSize: '0.8rem', color: colors.textMuted, mb: 1.5 }}>
                          Nenhum comentário ainda.
                        </Typography>
                      ) : (
                        threadComments.map(c => (
                          <Box
                            key={c.id}
                            sx={{ mb: 1, backgroundColor: '#f8f9fa', border: '1px solid #f1f3f5', borderRadius: '8px', padding: '0.6rem 0.8rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: colors.textPrimary }}>
                                  {c.createdByName}
                                </Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: colors.textMuted }}>
                                  {formatIsoDate(c.createdAt)} às {c.createdAt.slice(11, 16)}
                                </Typography>
                                {c.kind === 'inactivation' && (
                                  <Chip label="Inativação" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#fff5e0', color: '#d98c00' }} />
                                )}
                                {c.kind === 'reactivation' && (
                                  <Chip label="Reativação" size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#e8f6ec', color: '#28a745' }} />
                                )}
                                {ADMIN_KIND_LABELS[c.kind] && (
                                  <Chip
                                    label={ADMIN_KIND_LABELS[c.kind]}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#e7f0fd', color: '#1565c0' }}
                                  />
                                )}
                              </Box>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 400, color: colors.text, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                                {c.comment}
                              </Typography>
                            </Box>
                            {c.canDelete && (
                              <Tooltip title="Excluir meu comentário" arrow>
                                <IconButton size="small" onClick={() => setCommentToDelete(c)} sx={{ color: colors.textMuted, p: 0.25, mt: '-2px', '&:hover': { color: colors.error } }}>
                                  <Delete sx={{ fontSize: '0.95rem' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ))
                      )}
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Escreva um comentário..."
                          multiline
                          maxRows={4}
                          fullWidth
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          inputProps={{ maxLength: DIAGNOSIS_COMMENTS_MAX_LENGTH }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem', backgroundColor: '#fff' }, '& textarea': { minHeight: '0 !important', border: 'none !important', padding: '0 !important' } }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAddComment}
                          disabled={sendingComment || !newComment.trim()}
                          sx={{ backgroundColor: colors.primary, textTransform: 'none', flexShrink: 0, '&:hover': { backgroundColor: '#029AAB' } }}
                        >
                          Comentar
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Tooltip title={editHint(diagnosis)} arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!diagnosis.active || !diagnosis.canEdit}
                        onClick={() => openEdit(diagnosis)}
                        sx={{
                          color: '#2196f3',
                          border: '1px solid #e3f2fd',
                          width: 32,
                          height: 32,
                          '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#2196f3' },
                          '&.Mui-disabled': { color: '#b0bec5', borderColor: '#eceff1' },
                        }}
                      >
                        <Edit sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </div>
      )}

      {totalCount > 0 && !error && (
        <StandardPagination
          currentPage={page}
          totalPages={totalPages}
          itemsPerPage={pageSize}
          totalItems={totalCount}
          onPageChange={setPage}
          onItemsPerPageChange={(n) => { setPageSize(n); setPage(1); }}
        />
      )}

      {/* Modal de inserção/edição (padrão canônico) */}
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)', overflow: 'hidden', position: 'relative' } }}
      >
        <SavingOverlay open={saving} message="Gravando diagnóstico..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {editing ? 'Editar diagnóstico' : 'Novo diagnóstico'}
          </Typography>
          <IconButton onClick={closeModal} sx={{ color: colors.white, padding: '0.25rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem !important', paddingTop: '2rem !important' }}>
          {saveError && (
            <Box sx={{ backgroundColor: '#fde2e4', color: colors.error, borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', mb: 2 }}>
              {saveError}
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Autocomplete
              options={cidOptions}
              value={cid}
              filterOptions={(x) => x}
              getOptionLabel={(option) => cidLabel(option)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={cidSearch.trim().length < MIN_CID_SEARCH_LENGTH
                ? `Digite ao menos ${MIN_CID_SEARCH_LENGTH} caracteres`
                : 'Nenhum CID encontrado'}
              onChange={(_, newValue) => setCid(newValue)}
              onInputChange={(_, newInputValue) => setCidSearch(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="CID"
                  placeholder="Código (F840) ou descrição..."
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      fontSize: inputs.default.labelFontSize,
                      color: inputs.default.labelColor,
                      backgroundColor: inputs.default.labelBackground,
                      padding: inputs.default.labelPadding,
                    },
                  }}
                />
              )}
            />
            {editing?.requiresReason && (
              <TextField
                label="Justificativa da alteração"
                multiline
                minRows={2}
                fullWidth
                required
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                inputProps={{ maxLength: DIAGNOSIS_COMMENTS_MAX_LENGTH }}
                helperText="Este diagnóstico foi registrado por outra pessoa. A justificativa entra nos comentários e fica visível na tela."
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    fontSize: inputs.default.labelFontSize,
                    color: inputs.default.labelColor,
                    backgroundColor: inputs.default.labelBackground,
                    padding: inputs.default.labelPadding,
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { fontSize: inputs.default.fontSize },
                  '& textarea': { minHeight: '0 !important', border: 'none !important', padding: '0 !important' },
                }}
              />
            )}
            <TextField
              label="Observações"
              multiline
              minRows={4}
              fullWidth
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              inputProps={{ maxLength: DIAGNOSIS_COMMENTS_MAX_LENGTH }}
              helperText={`${comments.length}/${DIAGNOSIS_COMMENTS_MAX_LENGTH}`}
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': { fontSize: inputs.default.fontSize },
                // Neutraliza o CSS global de textarea (senão o textarea-sombra do MUI vaza)
                '& textarea': { minHeight: '0 !important', border: 'none !important', padding: '0 !important' },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
          <Button onClick={closeModal} disabled={saving} sx={{ color: colors.textSecondary, textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !cid || !dirty}
            sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
          >
            {editing ? 'Salvar' : 'Gravar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!toDeactivate}
        title="Inativar diagnóstico"
        message={toDeactivate ? `O diagnóstico CID ${toDeactivate.cidSubcategory} será inativado e passará a aparecer no filtro "Inativos". Você pode reativá-lo depois.` : ''}
        warningMessage="A justificativa entra nos comentários do diagnóstico, visíveis aos demais profissionais e administradores."
        confirmButtonText="Inativar"
        type="warning"
        isProcessing={deactivating}
        reasonLabel={toDeactivate?.requiresReason ? 'Justificativa' : 'Justificativa (opcional)'}
        reasonValue={deactivateReason}
        onReasonChange={setDeactivateReason}
        reasonRequired={!!toDeactivate?.requiresReason}
        reasonMaxLength={2000}
        onConfirm={handleDeactivate}
        onCancel={() => { if (!deactivating) { setToDeactivate(null); setDeactivateReason(''); } }}
      />

      <ConfirmModal
        isOpen={!!toReactivate}
        title="Reativar diagnóstico"
        message={toReactivate ? `O diagnóstico CID ${toReactivate.cidSubcategory} voltará a constar entre os ativos do paciente.` : ''}
        warningMessage="Este diagnóstico foi registrado por outra pessoa. A justificativa entra nos comentários e fica visível na tela."
        confirmButtonText="Reativar"
        type="warning"
        isProcessing={reactivating}
        reasonLabel="Justificativa"
        reasonValue={reactivateReason}
        onReasonChange={setReactivateReason}
        reasonRequired
        reasonMaxLength={2000}
        onConfirm={handleReactivateConfirm}
        onCancel={() => { if (!reactivating) { setToReactivate(null); setReactivateReason(''); } }}
      />

      <ConfirmModal
        isOpen={!!commentToDelete}
        title="Excluir comentário"
        message="Seu comentário será excluído definitivamente da thread deste diagnóstico."
        confirmButtonText="Excluir"
        type="danger"
        isProcessing={deletingComment}
        onConfirm={handleDeleteComment}
        onCancel={() => { if (!deletingComment) setCommentToDelete(null); }}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </div>
  );
};

export default PatientDiagnosisTab;
